import React, { useState, useEffect } from "react";
import Select from "react-select";
import { IoIosClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import apiInterceptor from "services/apiInterceptor";
import { KTIcon } from "helpers";
import { subscriptionApiEndpoint } from "constant/apiConstants";
import { useCommonContext } from "hooks‬/CommonContext";
import { useNavigate } from "react-router-dom";
import { privateRoutes } from "constant/route/privateRoutes";
import { useAuth } from "hooks‬/Auth";
import { v4 as uuidv4 } from "uuid";
import { FaExclamationTriangle } from "react-icons/fa";
import "pages/shared/ManagementTheme.scss";

// Sapiens users are managed from within Sapiens itself, not invited here.
// Any other product is fair game, so we exclude by name rather than
// hardcoding an allow-list of one.
const EXCLUDED_PRODUCT_NAME = "SAPIENS";

// Bootstrap's badge-light-info/badge-light-danger dark-mode pairings only
// hit ~3.1:1 / ~3.5:1 contrast (below WCAG AA 4.5:1) — these are our own
// light/dark pairs, each verified >=6.4:1 in both themes.
const CHIP_VARIANTS = ["blue", "green", "amber", "rose"];

interface Role {
  name: string;
  guid: string;
}

interface ProductRoleMap {
  [product: string]: Role[];
}

interface UserGroup {
  id: string;
  emailIds: string[];
  emailInput: string;
  roleGuid: string;
  product: string;
  emailError?: string;
  productError?: string;
  roleError?: string;
}

const generateId = () => uuidv4();

const UserInvitationForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccessToast, showErrorToast } = useCommonContext();
  const [useAvailableCount, setUseAvailableCount] = useState(0);
  const [maxInviteError, setMaxInviteError] = useState<string | null>(null);
  const [apiErrorData, setApiErrorData] = useState<any>([]);
  const { companySubscriptionDetails, fetchSubcriptionDetail } = useAuth();
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [products, setProducts] = useState<{ label: string; value: string }[]>(
    []
  );
  const [productRoles, setProductRoles] = useState<ProductRoleMap>({});

  useEffect(() => {
    setUseAvailableCount(
      companySubscriptionDetails?.companySubscriptionUsage?.find(
        (usage: any) => usage?.features?.featuresName === "ADD_USERS"
      )?.totalAvailableCount ?? 0
    );
  }, [companySubscriptionDetails]);

  const [formSubmitting, setFormSubmitting] = useState(false);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([
    {
      id: generateId(),
      emailIds: [],
      emailInput: "",
      roleGuid: "",
      product: "",
    },
  ]);

  // ----------------------------
  // Email input helpers
  // ----------------------------
  const COMMIT_KEYS = new Set(["Enter", " ", "Tab", ",", ";"]);

  const addEmailsToGroup = (groupIndex: number, raw: string) => {
    const updated = [...userGroups];
    const group = updated[groupIndex];
    const parts = raw
      .split(/[,\s;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    let anyInvalid = false;

    for (const email of parts) {
      const validationError = validateEmail(email);
      if (validationError) {
        group.emailError = validationError;
        anyInvalid = true;
        continue;
      }
      const currentTotal = updated.reduce((acc, g) => acc + g.emailIds.length, 0);
      if (currentTotal >= useAvailableCount) {
        setMaxInviteError(
          `You've reached the maximum of ${useAvailableCount} user${useAvailableCount > 1 ? "s" : ""
          } allowed on your current plan.
To invite more users, please upgrade your subscription.`
        );
        break;
      }
      if (!group.emailIds.includes(email)) group.emailIds.push(email);
    }

    group.emailInput = "";
    if (
      updated.reduce((acc, g) => acc + g.emailIds.length, 0) < useAvailableCount
    ) {
      setMaxInviteError(null);
    }
    if (!anyInvalid) group.emailError = "";
    setUserGroups(updated);
  };

  const handleEmailKeyDown = (
    groupIndex: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (COMMIT_KEYS.has(e.key)) {
      e.preventDefault();
      const input = userGroups[groupIndex].emailInput;
      if (input.trim()) addEmailsToGroup(groupIndex, input);
    }
  };

  const handleEmailBlur = (groupIndex: number) => {
    const input = userGroups[groupIndex].emailInput;
    if (input.trim()) addEmailsToGroup(groupIndex, input);
  };

  const handleEmailPaste = (
    groupIndex: number,
    e: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const text = e.clipboardData.getData("text");
    if (text) {
      e.preventDefault();
      addEmailsToGroup(groupIndex, text);
    }
  };

  // ----------------------------
  // API calls
  // ----------------------------
  const fetchProducts = async () => {
    try {
      const response = await apiInterceptor.get(
        subscriptionApiEndpoint.ENDPOINT_PRODUCTS
      );
      const productsData = response.data.data || [];
      const eligibleProducts = productsData.filter(
        (product: any) =>
          (product?.productTypeDTO?.name ?? "").toUpperCase() !==
          EXCLUDED_PRODUCT_NAME
      );
      setProducts(
        eligibleProducts.map((product: any) => ({
          label: product.productTypeDTO.displayName,
          value: product.productTypeDTO.guid,
        }))
      );
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setProductsLoaded(true);
    }
  };

  const fetchRoles = async (productGuid: string) => {
    try {
      const response = await apiInterceptor.get(
        `${subscriptionApiEndpoint.ENDPOINT_ALL_PRODUCTS}/${productGuid}/roles`
      );
      const roles = response.data.data || [];
      return roles.map((role: any) => ({
        name: role.displayName,
        guid: role.guid,
      }));
    } catch (error) {
      console.error("Error fetching roles:", error);
      return [];
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // ----------------------------
  // Validation & handlers
  // ----------------------------
  const validateEmail = (email: string): string | null => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!email) return null;
    if (!emailRegex.test(email))
      return t("messages:ENTER.VALID") + " " + t("EMAIL");
    return null;
  };

  const validateGroupFields = (group: UserGroup): boolean => {
    let isValid = true;
    if (!group.product) {
      group.productError = "Please select a product";
      isValid = false;
    } else group.productError = undefined;
    if (!group.roleGuid) {
      group.roleError = "Please select a role";
      isValid = false;
    } else group.roleError = undefined;
    setUserGroups([...userGroups]);
    return isValid;
  };

  const handleProductChange = async (index: number, selected: any) => {
    const selectedProductGuid = selected?.value || "";
    const updated = [...userGroups];
    updated[index].product = selectedProductGuid;
    updated[index].roleGuid = "";
    if (selectedProductGuid) {
      const roles = await fetchRoles(selectedProductGuid);
      setProductRoles((prev) => ({ ...prev, [selectedProductGuid]: roles }));
    }
    updated[index].productError = undefined;
    setUserGroups(updated);
  };

  const handleRoleChange = (index: number, selected: any) => {
    const updated = [...userGroups];
    updated[index].roleGuid = selected?.value || "";
    updated[index].roleError = undefined;
    setUserGroups(updated);
  };

  const handleEmailInputChange = (groupIndex: number, value: string) => {
    const updated = [...userGroups];
    updated[groupIndex].emailInput = value;
    if (updated[groupIndex].emailError) updated[groupIndex].emailError = "";
    setUserGroups(updated);
  };

  const removeEmail = (groupIndex: number, email: string) => {
    const updated = [...userGroups];
    updated[groupIndex].emailIds = updated[groupIndex].emailIds.filter(
      (e) => e !== email
    );
    if (updated[groupIndex].emailIds.length === 0) updated.splice(groupIndex, 1);
    if (updated.length === 0)
      updated.push({
        id: generateId(),
        emailIds: [],
        emailInput: "",
        roleGuid: "",
        product: "",
      });
    setUserGroups(updated);
  };

  const addUserGroup = () => {
    const hasEmpty = userGroups.some((g) => g.emailIds.length === 0);
    if (!hasEmpty)
      setUserGroups([
        ...userGroups,
        {
          id: generateId(),
          emailIds: [],
          emailInput: "",
          roleGuid: "",
          product: "",
        },
      ]);
  };

  const handleSubmit = async () => {
    const validGroups = userGroups.filter(
      (g) => g.emailIds.length > 0 && validateGroupFields(g)
    );
    const payload = validGroups.map((g) => ({
      emailIds: g.emailIds,
      userRoleGuid: g.roleGuid,
    }));
    if (payload.length === 0) {
      showErrorToast("Please add at least one valid user");
      return;
    }
    setFormSubmitting(true);
    try {
      const response = await apiInterceptor.post(
        subscriptionApiEndpoint.USER_INVITE,
        payload
      );
      if (response?.data?.errors?.length > 0) {
        showErrorToast("Error sending invitation");
        setApiErrorData(response.data.errors);
      } else {
        showSuccessToast("Invitation sent successfully");
        navigate(privateRoutes.userManagementUsers);
      }
      fetchSubcriptionDetail();
    } catch (e) {
      console.error("Error sending invitation:", e);
      showErrorToast("Error sending invitation");
    } finally {
      setFormSubmitting(false);
    }
  };

  // ----------------------------
  // Error list view
  // ----------------------------
  if (apiErrorData.length > 0) {
    return (
      <div className="ni-mgmt-page card mx-auto mt-4 mw-500px">
        <div className="card-body">
          <h3 className="card-title fw-normal mb-5 text-danger">
            Some invitations couldn’t be sent
          </h3>
          <ul className="list-group list-group-flush">
            {apiErrorData.map((err: any) => (
              <li
                key={err.statusOnActionName + err.statusDesc}
                className="list-group-item d-flex flex-column align-items-start bg-light border-danger border-start border-4 border-0 mb-2"
              >
                <strong className="text-dark">{err.statusOnActionName}</strong>
                <span className="text-danger small">{err.statusDesc}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="m-auto">
          <button
            className="btn btn-primary mx-3 mb-10"
            onClick={() => navigate(privateRoutes.userManagementUsers)}
          >
            Go to User List
          </button>
        </div>
      </div>
    );
  }

  // ----------------------------
  // Main render
  // ----------------------------
  if (productsLoaded && products.length === 0) {
    return (
      <div className="ni-mgmt-page container mt-4">
        <h5 className="ni-invite-title">Add Users</h5>
        <div className="card ni-invite-card p-8 text-center">
          <FaExclamationTriangle className="text-warning fs-1 mx-auto mb-3" />
          <div className="fw-bold mb-2">No products available for invitation</div>
          <div className="text-muted mb-5">
            None of your subscribed products currently support inviting users
            from this page.
          </div>
          <button
            className="btn btn-sm btn-light m-auto"
            onClick={() => navigate(privateRoutes.userManagementUsers)}
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ni-mgmt-page container mt-4">
      <h5 className="ni-invite-title">Add Users</h5>
      {products.length > 0 && (
        <div className="small text-muted mb-4">
          Currently available for: {products.map((p) => p.label).join(", ")}.
        </div>
      )}
      <div className="card mb-4 ni-invite-card">
        <div className="d-flex w-100 border-bottom ni-invite-header-row">
          <div className="p-4 p-xl-5 px-xxl-8 py-xxl-4 border-end" style={{ width: "60%" }}>
            <div className="pt-1">Email</div>
          </div>
          <div className="p-4 p-xl-5 px-xxl-8 py-xxl-3 border-end" style={{ width: "20%" }}>
            <div className="pt-1">Product</div>
          </div>
          <div className="p-4 p-xl-5 px-xxl-8 py-xxl-3" style={{ width: "20%" }}>
            <div className="pt-1">Role</div>
          </div>
        </div>

        {userGroups.map((group, groupIndex) => {
          const roles = productRoles[group.product] || [];
          const roleOptions = roles.map((r) => ({ label: r.name, value: r.guid }));

          return (
            <div className="d-flex w-100 border-bottom ni-invite-row" key={group.id}>
              <div className="p-4 p-xl-5 p-xxl-8 border-end" style={{ width: "60%" }}>
                <div className="d-flex flex-wrap gap-2 mb-1">
                  {group.emailIds.map((email, i) => {
                    const chipVariant =
                      CHIP_VARIANTS[i % CHIP_VARIANTS.length];

                    return (
                      <span
                        key={email}
                        className={`badge rounded px-3 d-flex fw-normal align-items-center pe-1 ni-invite-chip ni-invite-chip-${chipVariant}`}
                      >
                        {email}
                        <button
                          type="button"
                          className="btn-sm py-0 ms-2 bg-transparent border-0 fs-6 ni-invite-chip-remove"
                          onClick={() => {
                            removeEmail(groupIndex, email);
                            setMaxInviteError(null);
                          }}
                        >
                          <IoIosClose className="fs-2" />
                        </button>
                      </span>
                    );
                  })}

                  <input
                    type="email"
                    className={`form-control-sm border-0 input-email-invite ${group.emailError ? "is-invalid" : ""
                      }`}
                    style={{ paddingLeft: 0 }}
                    placeholder="Type email here..."
                    value={group.emailInput}
                    onChange={(e) => handleEmailInputChange(groupIndex, e.target.value)}
                    onKeyDown={(e) => handleEmailKeyDown(groupIndex, e)}
                    onBlur={() => handleEmailBlur(groupIndex)}
                    onPaste={(e) => handleEmailPaste(groupIndex, e)}
                  />
                  {group.emailError && (
                    <div className="invalid-feedback d-block">{group.emailError}</div>
                  )}
                </div>

                {/* ⬇ helper placed outside flex row, fully left-aligned */}
                {!group.emailError && (
                  <div className="small text-muted text-start mt-1 w-100" style={{ textAlign: "left" }}>
                    Press Enter/Tab/Space/Comma/Semicolon to add, or paste multiple emails.
                  </div>
                )}
              </div>

              <div className="p-4 p-xl-5 p-xxl-8 border-end" style={{ width: "20%" }}>
                <Select
                  options={products}
                  value={products.find((p) => p.value === group.product) || null}
                  onChange={(selected) => handleProductChange(groupIndex, selected)}
                  placeholder="Select product..."
                  className="react-select-styled p-0 react-select-sm border-select"
                  classNamePrefix="react-select"
                />
                {group.productError && (
                  <div className="invalid-feedback d-block">{group.productError}</div>
                )}
              </div>

              <div className="p-4 p-xl-5 p-xxl-8" style={{ width: "20%" }}>
                <Select
                  options={roleOptions}
                  value={roleOptions.find((r) => r.value === group.roleGuid) || null}
                  onChange={(selected) => handleRoleChange(groupIndex, selected)}
                  isDisabled={!group.product}
                  placeholder="Select role..."
                  className="react-select-styled p-0 react-select-sm border-select"
                  classNamePrefix="react-select"
                />
                {group.roleError && (
                  <div className="invalid-feedback d-block">{group.roleError}</div>
                )}
              </div>
            </div>
          );
        })}

        {maxInviteError && (
          <div className="alert alert-warning mx-5 my-10 d-flex align-items-center pb-5">
            <FaExclamationTriangle className="me-2 text-warning fs-4" />
            <div className="pt-1">{maxInviteError}</div>
          </div>
        )}

        <div>
          <button
            className="btn btn-sm btn-light-primary border-1 border-primary m-5 ni-invite-add-btn"
            onClick={addUserGroup}
            disabled={userGroups.some((g) => g.emailIds.length === 0)}
          >
            <KTIcon iconName="plus-square" className="fs-3" /> Add more users
          </button>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-4 me-4 ni-invite-actions">
        <button
          className="btn btn-sm btn-light"
          onClick={() => navigate(privateRoutes.userManagementUsers)}
        >
          Cancel
        </button>
        <button
          className="btn btn-sm btn-primary"
          disabled={formSubmitting}
          onClick={handleSubmit}
        >
          Send Invitation
        </button>
      </div>
    </div>
  );
};

export default UserInvitationForm;
